import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Download, KeyRound, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useSchedule } from "@/context/ScheduleContext";
import { useCart } from "@/context/CartContext";
import { api, errMsg } from "@/api";
import { formatAccessCode } from "@/lib/utils";
import StatusBadge from "@/components/StatusBadge";
import PhotoHandover from "@/components/PhotoHandover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import ResponsiveFormDialog from "@/components/ResponsiveFormDialog";
import ConfirmDialog from "@/components/admin/ConfirmDialog";

const TZ = { timeZone: "Asia/Jakarta" };
const fmt = (iso) =>
  new Date(iso).toLocaleString("id-ID", { ...TZ, day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("id-ID", { ...TZ, hour: "2-digit", minute: "2-digit" });
const fmtDate = (iso) => new Date(iso).toLocaleDateString("id-ID", { ...TZ, day: "2-digit", month: "long", year: "numeric" });

const CONDITIONS = [
  { key: "BAIK", label: "Kondisi baik" },
  { key: "RUSAK_RINGAN", label: "Rusak ringan" },
  { key: "RUSAK_BERAT", label: "Rusak berat" },
  { key: "HILANG", label: "Hilang" },
];

export default function BookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [condition, setCondition] = useState("BAIK");
  const [notes, setNotes] = useState("");
  const [returnOpen, setReturnOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const location = useLocation();
  const schedule = useSchedule();
  const cart = useCart();
  useEffect(() => {
    if (!location.state?.clearBookingDraft) return;
    schedule.clear(); cart.clear();
    navigate(location.pathname, { replace: true, state: { ...location.state, clearBookingDraft: false } });
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // Konteks admin: dipanggil dari /admin/* dengan state {backTo, backLabel}.
  // Default peminjam tidak berubah.
  const backTo = location.state?.backTo ?? "/peminjaman";
  const backLabel = location.state?.backLabel ?? "Booking Saya";
  // Halaman yang sama dipakai admin (/admin/...) dan peminjam (/peminjaman).
  // Admin tetap gelap; peminjam ikut theme terang borrower.
  const admin = location.pathname.startsWith("/admin");
  const T = admin ? {
    sub: "text-zinc-500", body: "text-zinc-400", heading: "text-zinc-100",
    card: "rounded-2xl border border-white/10 bg-zinc-900 p-8",
    row: "bg-zinc-950/60", rowText: "text-zinc-400",
    lockedBox: "border-white/10 bg-zinc-900", lockedCode: "text-zinc-600",
    back: "text-zinc-400 hover:text-white", cancelBtn: "border-white/10 bg-transparent text-zinc-300 hover:bg-white/10",
    label: "text-zinc-500", input: "border-white/10 bg-zinc-950",
    condOff: "border-white/10 hover:border-white/20",
    checkRow: "bg-zinc-950/60 hover:bg-zinc-950", checkRowOff: "bg-zinc-950/30 opacity-60",
  } : {
    sub: "text-slate-500", body: "text-slate-600", heading: "text-slate-900",
    card: "rounded-lg border border-slate-200 bg-white p-5 sm:p-8",
    row: "bg-slate-100", rowText: "text-slate-500",
    lockedBox: "border-slate-200 bg-white", lockedCode: "text-slate-300",
    back: "text-[#004f9e] hover:underline", cancelBtn: "border-slate-300 bg-white text-slate-700 hover:border-slate-900",
    label: "text-slate-500", input: "border-slate-300 bg-white text-slate-900",
    condOff: "border-slate-300 hover:border-slate-900",
    checkRow: "bg-slate-100 hover:bg-slate-200/60", checkRowOff: "bg-slate-100/60 opacity-60",
  };

  const load = () => api.get(`/bookings/${id}`).then(({ data }) => setB(data)).catch((e) => toast.error(errMsg(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    (b?.lines || []).forEach((l) => { (g[l.category || "Lainnya"] = g[l.category || "Lainnya"] || []).push(l); });
    return g;
  }, [b]);

  const downloadPdf = async () => {
    const { default: jsPDF } = await import("jspdf");
    const doc = new jsPDF();
    let y = 20;
    const line = (t, size = 11, bold = false) => {
      doc.setFontSize(size);
      doc.setFont("helvetica", bold ? "bold" : "normal");
      doc.text(t, 15, y);
      y += size < 13 ? 7 : 10;
    };
    line(b.booking_type === "LOCATION_ONLY" ? "BUKTI BOOKING ACARA" : "BUKTI PEMINJAMAN ALAT", 17, true);
    line(`No. Invoice: INV-${b.code}`);
    y += 3;
    line(`Nama: ${b.user_name}`);
    line(`Mulai: ${fmt(b.start_time)}`);
    line(`Selesai: ${fmt(b.end_time)}`);

    line(`Acara: ${b.purpose}`);
    line(`Lokasi: ${b.location_name_snapshot || b.custom_location || "Legacy"}`);
    line(`Durasi terjadwal: ${((Date.parse(b.end_time)-Date.parse(b.start_time))/3600000).toFixed(1)} jam`);
    y += 3;
    line("LIST PEMINJAMAN", 13, true);
    Object.entries(grouped).forEach(([cat, list]) => {
      line(cat, 11, true);
      list.forEach((l) => line(`   ${l.name} (${l.qty} pcs) - ${l.item_code}`));
    });
    y += 3;
    if (b.access_codes?.length) {
      line("AKSES STORAGE", 13, true);
      b.access_codes.forEach((ac) => {
        line(`${ac.kind === "RETURN" ? "Pengembalian" : ac.kind === "PICKUP" ? "Pengambilan" : "Akses"}: ${ac.released ? ac.code : "dikirim 1 jam sebelum jadwal"}`);
        line(`   Jadwal: ${fmt(ac.schedule_at)}`);
      });
    }
    line(`Status: ${b.status}`, 11, true);
    doc.save(`invoice-${b.code}.pdf`);
  };

  if (!b) return <p className="text-sm text-zinc-500">Memuat…</p>;

  const equipment = b.booking_type !== "LOCATION_ONLY";
  const canReturn = equipment && ["APPROVED", "BORROWED", "OVERDUE"].includes(b.status);
  const canCancel = ["PENDING", "APPROVED"].includes(b.status);
  const accessReview = ["NEEDS_REVIEW", "REVIEW_CLOSED"].includes(b.access_sync_state);

  return (
    <div className={admin ? "space-y-8" : "borrower-light space-y-6 pb-24 text-slate-900"}>
      <button data-testid="back-button" onClick={() => navigate(backTo)}
        className={`flex min-h-[44px] items-center gap-2 text-sm font-semibold ${T.back}`}>
        <ArrowLeft className="h-4 w-4" /> {backLabel}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className={`font-mono text-xs ${T.sub}`}>BOOKING {b.code}</p>
          <h1 data-testid="booking-detail-title" className={`mt-2 font-heading ${admin ? "text-3xl font-bold tracking-tighter sm:text-4xl" : "text-[26px] font-semibold tracking-tight text-slate-900"}`}>
            {b.purpose}
          </h1>
          <p className={`mt-3 text-sm ${T.body}`}>
            Mulai {fmt(b.start_time)} / selesai {fmt(b.end_time)} / {equipment ? `${b.total_qty} pcs` : "Tanpa barang"}
          </p>
          <p className={`mt-2 text-sm ${T.body}`}>{b.location_name_snapshot || b.custom_location || "Lokasi legacy"} · Acara: {({ PENDING: "Menunggu persetujuan", REJECTED: "Ditolak", CANCELLED: "Dibatalkan", COMPLETED: "Selesai", IN_PROGRESS: "Berlangsung", SCHEDULED: "Terjadwal" }[b.event_status || b.status]) || ( Date.now() >= Date.parse(b.end_time) ? "Selesai" : Date.now() >= Date.parse(b.start_time) ? "Berlangsung" : "Terjadwal")}</p>
        </div>
        <StatusBadge status={b.status} testId="booking-detail-status" tone={admin ? "dark" : "light"} />
      </div>

      {admin && equipment && accessReview && (
        <section aria-labelledby="access-review-heading" className="rounded-2xl border border-amber-500/40 bg-zinc-900 p-5 sm:p-8">
          <h2 id="access-review-heading" className="text-lg font-semibold text-amber-300">{b.access_sync_state === "NEEDS_REVIEW" ? "Akses perlu diperiksa" : "Pemeriksaan dicatat, akses tetap diblokir"}</h2>
          <p className="mt-2 text-sm text-zinc-200">{b.access_review_reason === "SYNC_TIMEOUT" ? "Sinkronisasi terhenti atau melewati batas waktu; hasil operasi provider tidak diketahui." : "Hasil operasi akses belum dapat dipastikan."}</p>
          <p className="mt-2 text-sm text-zinc-400">Periksa provider dan perangkat fisik. PIN mungkin masih berlaku. Jangan membuat PIN baru sebelum ada prosedur rekonsiliasi yang aman.</p>
          {b.access_sync_state === "NEEDS_REVIEW" && <Button disabled={busy} onClick={() => setReviewOpen(true)} className="mt-4">Catat pemeriksaan, tetap blokir</Button>}
          {b.access_sync_state === "REVIEW_CLOSED" && <p className="mt-3 text-sm text-zinc-400">Keputusan admin: pemeriksaan selesai, pembuatan dan pengiriman PIN tetap diblokir. Status PIN fisik tidak disimpulkan.</p>}
        </section>
      )}
      <ConfirmDialog open={reviewOpen} onOpenChange={setReviewOpen} title="Catat pemeriksaan akses?" description="Pastikan Anda sudah memeriksa provider dan perangkat fisik. Keputusan ini tidak menonaktifkan PIN fisik. Pembuatan dan pengiriman PIN tetap diblokir." confirmLabel="Provider dan perangkat sudah diverifikasi, tetap blokir" pending={busy} danger={false} onConfirm={async () => {
        setBusy(true);
        try { await api.post(`/admin/bookings/${id}/access-review`, { decision: "PROVIDER_VERIFIED_KEEP_BLOCKED" }); setReviewOpen(false); toast.success("Pemeriksaan dicatat. Akses tetap diblokir."); await load(); }
        catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
      }} />

      {!accessReview && equipment && (b.access_codes?.length ? b.access_codes : []).length > 0 && b.status !== "PENDING" ? (
        <div className="grid gap-6 lg:grid-cols-2">
          {b.access_codes.map((ac) => {
            const label = ac.kind === "RETURN" ? "Pengembalian" : ac.kind === "PICKUP" ? "Pengambilan" : "Akses";
            return (
              <div key={ac.id} data-testid={`access-card-${ac.kind.toLowerCase()}`}
                className={`${admin ? "rounded-2xl border p-8" : "rounded-lg border p-5 sm:p-8"} ${ac.released ? "border-emerald-500/30 bg-emerald-500/5" : T.lockedBox}`}>
                <p className={`flex items-center gap-2 text-xs uppercase tracking-[0.2em] ${ac.released ? (admin ? "text-emerald-400" : "text-emerald-700") : T.sub}`}>
                  {ac.released ? <KeyRound className="h-4 w-4" /> : <Lock className="h-4 w-4" />} Kode {label}
                </p>
                {ac.released ? (
                  <>
                    <p data-testid={`access-code-${ac.kind.toLowerCase()}`}
                      className={`mt-6 font-mono text-4xl font-bold tracking-[0.2em] sm:text-5xl ${admin ? "text-emerald-400" : "text-emerald-700"}`}>
                      {formatAccessCode(ac.code)}
                    </p>
                    <p className={`mt-4 text-sm ${T.body}`}>
                      Berlaku {fmt(ac.valid_from)} – {fmtTime(ac.valid_until)}
                    </p>
                    <Button data-testid={`copy-access-code-${ac.kind.toLowerCase()}`}
                      onClick={() => { navigator.clipboard?.writeText(ac.code); toast.success("Kode disalin"); }}
                      className="mt-6 h-12 rounded-full bg-emerald-500 px-6 font-semibold text-black hover:bg-emerald-400">
                      <Copy className="mr-2 h-4 w-4" /> Copy Code
                    </Button>
                  </>
                ) : (
                  <>
                    <p className={`mt-6 font-mono text-4xl font-bold tracking-[0.3em] sm:text-5xl ${T.lockedCode}`}>• • • •</p>
                    <p data-testid={`access-locked-${ac.kind.toLowerCase()}`} className={`mt-4 text-sm ${T.body}`}>
                      Kode muncul otomatis 1 jam sebelum jadwal pengambilan ({fmt(ac.schedule_at)}),
                      yaitu sekitar {fmt(ac.release_at)}.
                    </p>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : accessReview ? (!admin && <p className={T.body}>Akses gudang sedang diperiksa oleh admin. Hubungi admin untuk informasi lebih lanjut.</p>) : equipment ? (
        <div className={T.card}>
          <p className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Akses Storage</p>
          <p className={`mt-4 text-base ${T.body}`}>
            Satu kode pintu untuk ambil &amp; kembali dibuat setelah booking disetujui admin, lalu muncul 1 jam sebelum jadwal pengambilan.
          </p>
        </div>
      ) : <p className={T.body}>Tanpa barang · tidak ada akses gudang atau pengembalian alat.</p>}

      <div data-testid="invoice-card" className={T.card}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className={`font-heading ${admin ? "text-2xl" : "text-[19px] text-slate-900"} font-semibold tracking-tight`}>Bukti Booking</h2>
            <p className={`mt-2 text-sm ${T.sub}`}>Ringkasan jadwal bersama dan lokasi acara.</p>
          </div>
          <Button data-testid="download-invoice-button" onClick={downloadPdf} variant="outline"
            className={admin ? "rounded-full border-white/10 bg-transparent text-zinc-200 hover:bg-white/10" : "rounded-full border-slate-300 bg-white text-slate-700 hover:border-slate-900"}>
            <Download className="mr-2 h-4 w-4" /> PDF
          </Button>
        </div>

        <dl className={`mt-8 grid gap-6 sm:grid-cols-2 ${admin ? "" : "text-slate-900"}`}>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Nama</dt><dd className="mt-2">{b.user_name}</dd></div>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Mulai</dt><dd className="mt-2">{fmt(b.start_time)}</dd></div>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Selesai</dt><dd className="mt-2">{fmt(b.end_time)}</dd></div>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Durasi terjadwal</dt>
            <dd className="mt-2">
              {((Date.parse(b.end_time) - Date.parse(b.start_time)) / 3600000).toFixed(1)} jam
            </dd>
          </div>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Acara</dt><dd className="mt-2">{b.purpose}</dd></div>
          <div><dt className={`text-xs uppercase tracking-[0.2em] ${T.sub}`}>Jenis / Lokasi</dt><dd className="mt-2">{equipment ? `${b.total_qty} pcs` : "Tanpa barang"} · {b.location_name_snapshot || b.custom_location || "Legacy"}</dd></div>
        </dl>

        <div className="mt-8 space-y-6">
          {equipment && <p className={`text-xs uppercase tracking-[0.2em] ${admin ? "text-zinc-400" : "text-slate-500"}`}>List Peminjaman</p>}
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} data-testid={`invoice-group-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`} className="space-y-2">
              <p className={`font-heading text-base font-semibold ${admin ? "text-zinc-200" : "text-slate-900"}`}>{cat}</p>
              {list.map((l) => (
                <div key={l.item_id} className={`flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm ${T.row}`}>
                  <span className={admin ? "" : "text-slate-900"}>{l.name}</span>
                  <span className={`font-mono ${T.rowText}`}>{l.qty} pcs · {l.item_code}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {b.calendar && (
          <p className={`mt-6 text-xs ${T.sub}`}>
            Google Calendar: {b.calendar.status}{b.calendar.simulated ? " (simulasi)" : ""}
          </p>
        )}
      </div>

      {equipment && ["APPROVED", "BORROWED", "OVERDUE", "RETURN_REQUESTED", "RETURNED"].includes(b.status) && (
        <div className="grid gap-6 lg:grid-cols-2">
          <PhotoHandover bookingId={id} phase="pickup" photos={b.pickup_photos}
            locked={["RETURNED"].includes(b.status)}
            lockedText="Booking sudah selesai, foto pengambilan dikunci."
            onSaved={setB} />
          <PhotoHandover bookingId={id} phase="return" photos={b.return_photos}
            locked={!b.checklist_unlocked || b.status === "RETURNED"}
            lockedText={b.status === "RETURNED" ? "Booking sudah selesai, foto pengembalian dikunci."
              : `Terbuka pada tanggal pengembalian (${fmtDate(b.end_time)}).`}
            onSaved={setB} />
        </div>
      )}

      {canReturn && (
        <div data-testid="return-checklist-card" className={T.card}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className={`font-heading ${admin ? "text-2xl" : "text-[19px] text-slate-900"} font-semibold tracking-tight`}>Checklist Pengembalian</h2>
              <p className={`mt-2 text-sm ${T.sub}`}>
                {b.checklist_unlocked
                  ? "Centang setiap alat yang sudah dikembalikan ke storage."
                  : `Terbuka pada hari pengembalian (${fmtDate(b.end_time)}).`}
              </p>
            </div>
            {!b.checklist_unlocked && (
              <span data-testid="checklist-locked-badge"
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${admin ? "border-zinc-500/30 bg-zinc-500/10 text-zinc-400" : "border-slate-300 bg-slate-100 text-slate-500"}`}>
                <Lock className="h-3 w-3" /> Terkunci
              </span>
            )}
          </div>

          <div className="mt-6 space-y-2">
            {b.checklist.map((row) => (
              <label key={row.item_id} data-testid={`checklist-row-${row.item_code}`}
                className={`flex items-center gap-4 rounded-xl px-4 py-3 ${b.checklist_unlocked ? T.checkRow : T.checkRowOff}`}>
                <Checkbox
                  data-testid={`checklist-checkbox-${row.item_code}`}
                  checked={!!row.checked}
                  disabled={!b.checklist_unlocked || busy}
                  onCheckedChange={(v) =>
                    act(() => api.post(`/bookings/${id}/checklist`, { item_id: row.item_id, checked: !!v }),
                      v ? `${row.name} dicentang` : `${row.name} dibatalkan`)}
                />
                <span className={`flex-1 text-sm ${admin ? "" : "text-slate-900"}`}>{row.name}</span>
                <span className={`font-mono text-xs ${T.rowText}`}>{row.qty} pcs</span>
              </label>
            ))}
          </div>

          {b.checklist_unlocked && !b.checklist_complete && (
            <p className={`mt-4 text-sm font-semibold ${admin ? "text-amber-400" : "text-amber-800"}`}>Centang semua alat dulu sebelum mengajukan pengembalian.</p>
          )}

          <div className="mt-6">
            <Button data-testid="open-return-dialog-button" disabled={!b.checklist_complete}
              onClick={() => setReturnOpen(true)}
              className="h-14 rounded-full bg-[#007AFF] px-8 text-base font-semibold text-white hover:bg-[#0069DB] disabled:opacity-40">
              Ajukan Pengembalian
            </Button>
            <ResponsiveFormDialog open={returnOpen} onOpenChange={setReturnOpen}
              tone={admin ? "dark" : "light"} title="Pengembalian Alat">
              <div className="space-y-4">
                <div className="grid gap-2">
                  {CONDITIONS.map((c) => (
                    <button key={c.key} data-testid={`condition-${c.key}`} onClick={() => setCondition(c.key)}
                      className={`rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-200 ${
                        condition === c.key ? "border-[#007AFF] bg-[#007AFF]/10" : T.condOff} ${admin ? "" : "text-slate-900"}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {condition !== "BAIK" && (
                  <Textarea data-testid="return-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                    placeholder="Catatan kerusakan / kehilangan"
                    className={`min-h-24 rounded-xl ${T.input}`} />
                )}
                <Button data-testid="submit-return-button" disabled={busy}
                  onClick={() => act(() => api.post(`/bookings/${id}/return`, { condition, notes }), "Permintaan pengembalian dikirim")}
                  className="h-12 w-full rounded-full bg-[#007AFF] font-semibold text-white hover:bg-[#0069DB]">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kirim"}
                </Button>
              </div>
            </ResponsiveFormDialog>
          </div>
        </div>
      )}

      {admin && ["APPROVED", "IN_PROGRESS", "BORROWED", "OVERDUE"].includes(b.status) && <div className={T.card}><h2 className="font-semibold">Perpanjang jadwal bersama</h2><p className={`mt-2 text-sm ${T.body}`}>Menambah 60 menit pada akhir acara, reservasi lokasi, dan barang jika ada. Konflik diperiksa kembali oleh server.</p><Button disabled={busy} onClick={() => { if (window.confirm("Perpanjang akhir jadwal bersama 60 menit?")) act(() => api.post(`/admin/bookings/${id}/extend`, { minutes: 60 }), "Jadwal diperpanjang"); }} className="mt-3">Perpanjang 60 menit</Button></div>}
      {canCancel && (
        <>
          <Button data-testid="cancel-booking-button" variant="outline" disabled={busy}
            onClick={() => setCancelOpen(true)}
            className={`h-14 rounded-full px-8 text-base ${T.cancelBtn}`}>
            Batalkan Booking
          </Button>
          <ResponsiveFormDialog open={cancelOpen} onOpenChange={setCancelOpen}
            tone={admin ? "dark" : "light"} title="Batalkan booking ini?"
            description={`Pengajuan "${b.purpose}" dibatalkan. Reservasi jadwal dan lokasi ikut dibebaskan.`}>
            <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
              <Button data-testid="cancel-cancel-booking-button" autoFocus variant="outline"
                onClick={() => setCancelOpen(false)}
                className={admin ? "h-12 rounded-full px-6" : "h-12 rounded-full border-slate-300 bg-white px-6 text-slate-900 hover:border-slate-900"}>
                Batal
              </Button>
              <Button data-testid="confirm-cancel-booking-button" disabled={busy}
                onClick={() => { setCancelOpen(false); act(() => api.post(`/bookings/${id}/cancel`), "Booking dibatalkan"); }}
                className="h-12 rounded-full bg-rose-600 px-6 font-semibold text-white hover:bg-rose-700">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ya, batalkan"}
              </Button>
            </div>
          </ResponsiveFormDialog>
        </>
      )}
    </div>
  );
}
